import { sgSupabase } from "../../lib/sgSupabaseClient";
import { assertCanUseSG } from "./access";
import type { CuadreAccount } from "./cuadre";
import { PLATFORMS, type SgPlatform } from "./platforms";

// Corregir el origen de las cuentas (fase 4 del cuadre): el origen elegido en
// SG pasa a ser donde de verdad están sus unidades. Decisión de Daniel
// (27-oct-2026): en lote sólo Wialon y Mapon. v5 se cambia cuenta por cuenta
// en el formulario, porque agregarlo o quitarlo crea o desvincula la compañía
// en v5 (/sg-sync/company-from-sg); por eso aquí v5 se conserva tal cual y
// sólo se avisa.

export interface OriginChange {
  id: number;
  name: string;
  before: SgPlatform[];
  after: SgPlatform[];
  // v5 no coincide y hay que cambiarlo a mano: agregarlo o quitarlo.
  v5Manual: "add" | "remove" | null;
}

export function proposeOrigin(sg: SgPlatform[], real: SgPlatform[]): Pick<OriginChange, "after" | "v5Manual"> {
  const after = PLATFORMS.filter((p) => (p === "v5" ? sg.includes("v5") : real.includes(p)));
  const v5Manual = real.includes("v5") && !sg.includes("v5") ? "add" : !real.includes("v5") && sg.includes("v5") ? "remove" : null;
  return { after, v5Manual };
}

const same = (a: SgPlatform[], b: SgPlatform[]) => a.length === b.length && a.every((p) => b.includes(p));

// Cambios de las cuentas descuadradas. `after` vacío no se aplica (SG exige al
// menos un origen): pasa cuando la cuenta sólo tiene unidades en v5 y en SG no
// lo tiene; ahí lo único que falta es agregar v5 a mano.
export function originChanges(accounts: CuadreAccount[]): OriginChange[] {
  return accounts
    .filter((a) => a.mismatch)
    .map((a) => ({ id: a.id, name: a.name, before: a.sgOrigin, ...proposeOrigin(a.sgOrigin, a.realOrigin) }))
    .filter((c) => c.v5Manual || (!same(c.before, c.after) && c.after.length > 0));
}

export const isApplicable = (c: OriginChange) => c.after.length > 0 && !same(c.before, c.after);

export interface ApplyResult {
  ok: number[];
  failed: { id: number; error: string }[];
}

// Agrupa por origen final: sólo hay 7 combinaciones posibles, así que el lote es
// un puñado de updates `in (ids)` en vez de uno por cuenta (uno por cuenta tardó
// ~1.3 s c/u contra el SG local: 192 cuentas = 4 min). Si un bloque falla, los
// demás se aplican y la pantalla dice cuáles no. Sin /sg-sync/company-from-sg a
// propósito: v5 no cambia.
const CHUNK = 200; // ids por request: la URL de PostgREST lleva la lista completa

export function groupByOrigin(changes: OriginChange[]): { origin: SgPlatform[]; ids: number[] }[] {
  const groups = new Map<string, { origin: SgPlatform[]; ids: number[] }>();
  for (const c of changes.filter(isApplicable)) {
    const key = c.after.join(",");
    const g = groups.get(key) ?? { origin: c.after, ids: [] };
    g.ids.push(c.id);
    groups.set(key, g);
  }
  return [...groups.values()].flatMap((g) =>
    Array.from({ length: Math.ceil(g.ids.length / CHUNK) }, (_, i) => ({ origin: g.origin, ids: g.ids.slice(i * CHUNK, (i + 1) * CHUNK) }))
  );
}

export async function applyOriginChanges(changes: OriginChange[], onProgress?: (done: number) => void): Promise<ApplyResult> {
  assertCanUseSG();
  const out: ApplyResult = { ok: [], failed: [] };
  for (const g of groupByOrigin(changes)) {
    const { error } = await sgSupabase.from("users").update({ origin: g.origin, updated_at: new Date().toISOString() }).in("id", g.ids);
    if (error) out.failed.push(...g.ids.map((id) => ({ id, error: error.message })));
    else out.ok.push(...g.ids);
    onProgress?.(out.ok.length + out.failed.length);
  }
  return out;
}
